/**
 * Configuration - Page settings for Control Centre and reports.
 * Optifleet manual-refresh range (org123 only), Fleet capital/operational cost
 * assumptions, Profitability assumptions (Costs — admin UI only), and Utilisation
 * (admin UI only; non-admins see Coming Soon).
 * Vehicle list and Home Depots live on the Assets page.
 *
 * Costs panel visibility is Cognito-admin UI only. Profitability config GET/PUT
 * stay available to authenticated tenants so Fleet Cost → Running Costs can
 * read/write the same store for non-admins.
 *
 * Note: live Control Centre polling (Samsara /vehicles/live) is a fixed 5-minute
 * poll and is not affected by the Optifleet setting below.
 */

import { Database, MapPin, CornerDownRight, Gauge, PoundSterling, SlidersHorizontal, Truck } from 'lucide-react';
import { getOrganizationIdFromToken } from '../auth/cognitoAuth';
import { useConfig } from '../contexts/ConfigContext';
import { isSessionAdmin } from '../utils/pageAccess';
import ProfitabilityAssumptionsPanel from '../components/profitability/ProfitabilityAssumptionsPanel';
import UtilisationAssumptionsPanel from '../components/profitability/UtilisationAssumptionsPanel';
import FleetCostAssumptionsTables from '../components/configuration/FleetCostAssumptionsTables';

/** Orgs that may configure legacy Optifleet manual-refresh range (dual telematics). */
const OPTIFLEET_MANUAL_REFRESH_ORGS = new Set(['org123']);

function readSessionOrganisationId(): string | null {
  try {
    const idToken = localStorage.getItem('zecure_cognito_id_token');
    if (!idToken) return null;
    return getOrganizationIdFromToken(idToken);
  } catch {
    return null;
  }
}

export default function Configuration() {
  const { config, setTelematics } = useConfig();
  const { telematics } = config;
  const organisationId = readSessionOrganisationId();
  const isAdmin = isSessionAdmin();
  const showOptifleetManualRefresh =
    isAdmin
    || (organisationId != null && OPTIFLEET_MANUAL_REFRESH_ORGS.has(organisationId));
  /** Admin UI only — see file header (API remains open for Running Costs). */
  const showCostsAssumptions = isAdmin;
  /** Admin UI only — non-admins see Coming Soon in place of the assumptions panel. */
  const showUtilisationAssumptions = isAdmin;

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="pb-2">
        <div className="flex items-center gap-2 mb-2">
          <SlidersHorizontal className="w-8 h-8 text-warning" />
          <h2 className="text-2xl font-bold text-white">Configuration</h2>
        </div>
        <p className="text-muted-foreground mt-1">
          {showOptifleetManualRefresh
            ? 'Page settings for Optifleet manual refresh, fleet costs, Profitability, and Utilisation.'
            : 'Cost and utilisation assumptions for reports.'}
          {' '}
          Manage vehicles and depots on the Assets page.
        </p>
      </div>

      {showOptifleetManualRefresh && (
        <>
          {/* Optifleet manual refresh range — org123 or Cognito admin */}
          <section className="pt-4 pb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-2 text-xl font-semibold text-white">
                  <Database className="w-5 h-5 text-ring flex-shrink-0" />
                  Control Centre
                </span>
                <span className="flex items-center gap-2 text-lg font-medium text-ring">
                  <CornerDownRight className="w-4 h-4 flex-shrink-0" />
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  Optifleet Manual Refresh Range
                </span>
              </div>
            </div>
            <div className="bg-card/50 rounded-lg p-5 border border-border">
              <p className="text-sm text-muted-foreground mb-3">
                How much history to pull on manual Optifleet refresh. Live Control Centre GPS
                polling (Samsara) uses a fixed 5-minute poll and ignores this setting.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Display Time Range</label>
                  <select
                    value={telematics.latestIntervalDisplay}
                    onChange={(e) => setTelematics({ latestIntervalDisplay: e.target.value })}
                    className="w-full max-w-xs bg-secondary border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-warning focus:border-warning"
                  >
                    <option value="1h">Last 1 hour</option>
                    <option value="6h">Last 6 hours</option>
                    <option value="12h">Last 12 hours</option>
                    <option value="24h">Last 24 hours</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          <hr className="border-0 border-t border-border my-4" />
        </>
      )}

      {/* Fleet capital + standing costs */}
      <section className="pt-4 pb-6">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-5 h-5 text-success flex-shrink-0" />
          <div>
            <h3 className="text-xl font-semibold text-white">Fleet Costs</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Capital and operational rates by body style or tonnage class — align with Costs and Assets.
            </p>
          </div>
        </div>
        <FleetCostAssumptionsTables />
      </section>

      <hr className="border-0 border-t border-border my-4" />

      {/* Profitability Report — admin UI only (API open for Running Costs) */}
      {showCostsAssumptions && (
        <>
          <section className="pt-4 pb-6">
            <div className="flex items-center gap-2 mb-4">
              <PoundSterling className="w-5 h-5 text-success flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-white">Costs</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Fuel, standing, and driver cost assumptions used by the Profitability report.
                </p>
              </div>
            </div>
            <ProfitabilityAssumptionsPanel visible />
          </section>

          <hr className="border-0 border-t border-border my-4" />
        </>
      )}

      {/* Utilisation — admin edits assumptions; non-admins see Coming Soon */}
      <section className="pt-4 pb-6">
        <div className="flex items-center gap-2 mb-4">
          <Gauge className="w-5 h-5 text-chart-10 flex-shrink-0" />
          <div>
            <h3 className="text-xl font-semibold text-white">Utilisation</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Targets and formula weights used by the Utilisation report.
            </p>
          </div>
        </div>
        {showUtilisationAssumptions ? (
          <UtilisationAssumptionsPanel visible mode="truth" />
        ) : (
          <div className="bg-card/50 rounded-lg p-8 border border-border flex items-center justify-center">
            <span className="text-sm font-medium text-muted-foreground">Coming Soon</span>
          </div>
        )}
      </section>
    </div>
  );
}
